/* ============================================================
   PROSPER — assessment engine, storage, nav drawer, recall pill
   Loaded site-wide by prosper-nav.liquid.
   ============================================================ */
(function () {
  'use strict';

  var K_ANS = 'prosper_assess_answers';
  var K_PROG = 'prosper_assess_program';
  var K_SEEN = 'prosper_assess_seen';

  /* --- storage that never throws ------------------------------ */
  function get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  function del(k) { try { window.localStorage.removeItem(k); } catch (e) {} }

  var P = (window.PROSPER = window.PROSPER || {});

  P.KEYS = { answers: K_ANS, program: K_PROG, seen: K_SEEN };

  P.loadAnswers = function () {
    var raw = get(K_ANS);
    if (!raw) return null;
    try {
      var a = JSON.parse(raw);
      if (!a || typeof a !== 'object') return null;
      if (!a.exp || !a.goal || !a.days || !a.commit) return null;
      return a;
    } catch (e) { return null; }
  };

  P.saveResult = function (answers, program) {
    set(K_ANS, JSON.stringify(answers));
    set(K_PROG, program);
    set(K_SEEN, '1');
  };

  P.clear = function () { del(K_ANS); del(K_PROG); del(K_SEEN); };

  P.loadProgram = function () { return get(K_PROG); };
  P.seen = function () { return get(K_SEEN) === '1'; };

  /* ============================================================
     RECOMMENDATION
     answers = {
       exp:    1 new | 2 inconsistent | 3 regular | 4 years
       goal:   'fat_loss' | 'muscle' | 'strength' | 'transform' | 'athleticism'
       days:   2 (2-3) | 3 (3-4) | 4 (4-5) | 5 (5-6+)
       commit: 1..5
       block:  'dont_know' | 'consistency' | 'no_results' |
               'no_structure' | 'accountability' | 'better_plan'
     }
     Returns one of: 'foundation' | 'build' | 'shred' | 'elite'
     ============================================================ */
  P.recommend = function (a) {
    var exp = a.exp, goal = a.goal, days = a.days, commit = a.commit, block = a.block;
    var t;

    /* 1 — base match from training experience and goal */
    if (exp === 1) {
      t = 'foundation';
    } else if (exp === 2) {
      t = goal === 'fat_loss' ? 'shred' : 'build';
    } else if (exp === 3) {
      if (goal === 'fat_loss') t = 'shred';
      else if (goal === 'strength' && commit >= 4 && days >= 4) t = 'elite';
      else t = 'build';
    } else {
      t = (goal === 'fat_loss' && !(commit === 5 && days >= 4)) ? 'shred' : 'elite';
    }

    /* 2 — obstacle tie-break: someone with patchy experience whose real
           problem is consistency or not knowing what to do is better
           served building the habit than being sold intensity */
    if (exp === 2 && (block === 'consistency' || block === 'dont_know') &&
        (goal === 'transform' || days <= 3)) {
      t = 'foundation';
    }

    /* 3 — capacity guards: never recommend a program they cannot run */
    if (t === 'elite' && (days < 4 || commit < 3)) t = goal === 'fat_loss' ? 'shred' : 'build';
    if (t === 'shred' && days < 3) t = exp >= 3 ? 'build' : 'foundation';
    if (t === 'build' && days < 3 && exp <= 2) t = 'foundation';

    /* 4 — commitment cap: don't sell intensity to someone still looking */
    if (commit <= 2) {
      if (exp <= 2) t = 'foundation';
      else if (t === 'elite') t = 'build';
    }

    return t;
  };

  /* --- human-readable echo of their own answers ---------------- */
  var EXP = {
    1: 'New to training',
    2: 'Some experience, inconsistent',
    3: 'Training regularly',
    4: 'Years of serious training'
  };
  var GOAL = {
    fat_loss: 'Losing body fat',
    muscle: 'Building muscle',
    strength: 'Building strength',
    transform: 'A full physique rebuild',
    athleticism: 'Overall fitness and athleticism'
  };
  var DAYS = { 2: '2–3 days a week', 3: '3–4 days a week', 4: '4–5 days a week', 5: '5–6+ days a week' };
  var COMMIT = {
    1: 'Exploring for now',
    2: 'Ready to get started',
    3: 'Ready to become consistent',
    4: 'Seriously committed',
    5: 'Making training a priority'
  };
  var BLOCK = {
    dont_know: 'Not knowing what to do',
    consistency: 'Staying consistent',
    no_results: 'Not seeing results',
    no_structure: 'No real structure',
    accountability: 'No accountability',
    better_plan: 'Nothing — just needs a better plan'
  };

  P.readout = function (a) {
    return [
      { k: 'Your level', v: EXP[a.exp] || '—' },
      { k: 'Your focus', v: GOAL[a.goal] || '—' },
      { k: 'Your week', v: DAYS[a.days] || '—' },
      { k: 'Where you are', v: COMMIT[a.commit] || '—' },
      { k: "What's been in the way", v: BLOCK[a.block] || '—' }
    ];
  };

  /* --- why this program fits: every line traceable to an answer -- */
  var LEVEL_OF = { foundation: 1, build: 2, shred: 2, elite: 3 };

  P.reasons = function (a, program) {
    var out = [];

    /* experience */
    if (program === 'foundation') {
      out.push(a.exp === 1
        ? 'Built for a first training block — nothing assumes you already know the lifts'
        : 'Starts from the basics, which is what makes an inconsistent stretch stick');
    } else if (program === 'build') {
      out.push(a.exp >= 3
        ? 'Pitched at someone already training regularly, not at a beginner'
        : 'Assumes you know your way around a gym but have been training without a plan');
    } else if (program === 'shred') {
      out.push('Assumes existing training experience, so the lifting stays heavy while you eat less');
    } else {
      out.push('Written for years of training behind you — periodised rather than another random split');
    }

    /* schedule — always true against the program's own frequency */
    var sched = {
      foundation: { 2: 'Three sessions a week fits the 2–3 days you said you have',
                    3: 'Three sessions a week leaves room inside the 3–4 days you have',
                    4: 'Three sessions a week is comfortably inside your 4–5 days',
                    5: 'Three focused sessions a week, well within the time you said you have' },
      build:      { 2: 'Four sessions a week — one more than you said, so plan for a busy fourth day',
                    3: 'Four sessions a week matches the 3–4 days you said you can train',
                    4: 'Four sessions a week sits inside the 4–5 days you have',
                    5: 'Four sessions a week, with room left over in your week' },
      shred:      { 2: 'Four to five sessions a week — plan to be at the top of your range',
                    3: 'Four to five sessions a week, at the upper end of the days you gave',
                    4: 'Four to five sessions a week matches your availability exactly',
                    5: 'Four to five sessions a week fits inside the days you can train' },
      elite:      { 2: 'Five to six sessions a week — more than the days you gave',
                    3: 'Five to six sessions a week — above the days you gave',
                    4: 'Five to six sessions a week, at the top of your 4–5 days',
                    5: 'Five to six sessions a week matches the days you said you can train' }
    };
    out.push(sched[program][a.days]);

    /* goal */
    var goalLine = {
      fat_loss: {
        foundation: 'Fat loss starts with training you actually repeat — plus the nutrition starter guide',
        build: 'Adding muscle now makes the later fat-loss block work better',
        shred: 'Built around your goal of losing body fat while holding onto muscle',
        elite: 'Includes cutting guidance alongside the training blocks'
      },
      muscle: {
        foundation: 'Builds the base that muscle gain later gets built on',
        build: 'Built around your goal of adding visible muscle',
        shred: 'Keeps the muscle you have while the body fat comes off',
        elite: 'Dedicated hypertrophy blocks inside the wider plan'
      },
      strength: {
        foundation: 'Teaches the main lifts properly before you try to load them',
        build: 'Progressive overload is written into the plan, not left to memory',
        shred: 'Lifting stays heavy on purpose, so strength holds',
        elite: 'Sequenced strength blocks rather than maxing out every week'
      },
      transform: {
        foundation: 'A rebuild starts with consistency — this is the first eight weeks of it',
        build: 'Twelve weeks is a realistic first chapter of a full rebuild',
        shred: 'Changes how you look fastest, by changing body fat first',
        elite: 'Sixteen weeks, sequenced end to end — the closest thing here to a full rebuild'
      },
      athleticism: {
        foundation: 'General strength and movement quality first',
        build: 'Balanced upper/lower work rather than a bodybuilding split',
        shred: 'Conditioning is scheduled rather than random',
        elite: 'Conditioning blocks sit alongside strength and hypertrophy'
      }
    };
    out.push(goalLine[a.goal][program]);

    /* obstacle */
    var blockLine = {
      dont_know: 'Every session is written out, so there is nothing left to decide',
      consistency: 'Fixed weekly structure is what makes consistency easier to hold',
      no_results: 'Progressive overload is built in, so the work actually accumulates',
      no_structure: 'This is the structure you said you were missing',
      accountability: 'Progress tracking gives you something to answer to week by week',
      better_plan: 'A written plan with a beginning, a middle and an exit'
    };
    if (a.block && blockLine[a.block]) out.push(blockLine[a.block]);

    return out.slice(0, 4);
  };

  /* --- level of each program, for the compare grid -------------- */
  P.level = function (p) { return LEVEL_OF[p] || 1; };

  /* ============================================================
     NAV — mobile drawer
     ============================================================ */
  function initDrawer() {
    var drawer = document.querySelector('[data-pr-drawer]');
    if (!drawer) return;
    var root = document.documentElement;
    var burger = document.querySelector('[data-pr-burger]');

    function open() {
      drawer.classList.add('is-open');
      root.classList.add('pr-lock');
      if (burger) burger.setAttribute('aria-expanded', 'true');
    }
    function close() {
      drawer.classList.remove('is-open');
      root.classList.remove('pr-lock');
      if (burger) burger.setAttribute('aria-expanded', 'false');
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-pr-burger]')) { e.preventDefault(); open(); }
      else if (e.target.closest('[data-pr-drawer-close]')) { close(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  /* ============================================================
     RECALL PILL — only once an assessment has actually been taken
     ============================================================ */
  function initRecall() {
    var pill = document.querySelector('[data-pr-recall]');
    if (!pill) return;
    var prog = P.loadProgram();
    if (!prog) return;
    if (get('prosper_recall_hidden') === '1') return;

    var link = pill.querySelector('[data-pr-recall-link]');
    var name = pill.querySelector('[data-pr-recall-name]');
    var data = {};
    try { data = JSON.parse(pill.getAttribute('data-pr-recall') || '{}'); } catch (e) {}
    var hit = data[prog];
    if (!hit) return;

    if (name) name.textContent = hit.title;
    if (link) link.setAttribute('href', hit.url);
    pill.classList.add('is-on');

    var x = pill.querySelector('[data-pr-recall-dismiss]');
    if (x) x.addEventListener('click', function () {
      pill.classList.remove('is-on');
      set('prosper_recall_hidden', '1');
    });
  }

  function boot() { initDrawer(); initRecall(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
